/* Organization, site and device group endpoints. docs/04-data-model.md F.3. */

import { request } from '../client';
import { isMockTransport } from '../config';
import type {
  DeviceGroup,
  DeviceGroupCreate,
  DeviceGroupUpdate,
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  Site,
  SiteCreate,
  SiteUpdate,
  Uuid,
} from '../types';

export async function listOrganizations(): Promise<readonly Organization[]> {
  if (isMockTransport()) {
    const { mockListOrganizations } = await import('@/mocks/handlers');
    return mockListOrganizations();
  }
  return request<readonly Organization[]>('/organizations');
}

export async function listSites(organizationId?: Uuid): Promise<readonly Site[]> {
  if (isMockTransport()) {
    const { mockListSites } = await import('@/mocks/handlers');
    return mockListSites(organizationId);
  }
  return request<readonly Site[]>('/sites', {
    query: organizationId === undefined ? {} : { organizationId },
  });
}

export async function listDeviceGroups(organizationId?: Uuid): Promise<readonly DeviceGroup[]> {
  if (isMockTransport()) {
    const { mockListDeviceGroups } = await import('@/mocks/handlers');
    return mockListDeviceGroups(organizationId);
  }
  return request<readonly DeviceGroup[]>('/device-groups', {
    query: organizationId === undefined ? {} : { organizationId },
  });
}

/* ---------------------------------------------------------------- writes */

/*
  All nine writes below need organization.manage, which is [dangerous] in the catalog
  (docs/07-identity-rbac.md I.2). Archive is a PATCH to a status rather than a DELETE,
  because the schema keeps a deleted_at column and nothing here hard-deletes: a customer
  record with devices, sessions and audit rows pointing at it cannot be removed without
  breaking the audit trail, which is append-only by design.
*/

export async function createOrganization(body: OrganizationCreate): Promise<Organization> {
  if (isMockTransport()) {
    const { mockCreateOrganization } = await import('@/mocks/handlers');
    return mockCreateOrganization(body);
  }
  return request<Organization>('/organizations', { method: 'POST', body });
}

export async function updateOrganization(
  organizationId: Uuid,
  body: OrganizationUpdate,
): Promise<Organization> {
  if (isMockTransport()) {
    const { mockUpdateOrganization } = await import('@/mocks/handlers');
    return mockUpdateOrganization(organizationId, body);
  }
  return request<Organization>(`/organizations/${organizationId}`, { method: 'PATCH', body });
}

export async function archiveOrganization(organizationId: Uuid): Promise<Organization> {
  if (isMockTransport()) {
    const { mockArchiveOrganization } = await import('@/mocks/handlers');
    return mockArchiveOrganization(organizationId);
  }
  return request<Organization>(`/organizations/${organizationId}/archive`, { method: 'POST' });
}

export async function createSite(body: SiteCreate): Promise<Site> {
  if (isMockTransport()) {
    const { mockCreateSite } = await import('@/mocks/handlers');
    return mockCreateSite(body);
  }
  return request<Site>('/sites', { method: 'POST', body });
}

export async function updateSite(siteId: Uuid, body: SiteUpdate): Promise<Site> {
  if (isMockTransport()) {
    const { mockUpdateSite } = await import('@/mocks/handlers');
    return mockUpdateSite(siteId, body);
  }
  return request<Site>(`/sites/${siteId}`, { method: 'PATCH', body });
}

export async function archiveSite(siteId: Uuid): Promise<void> {
  if (isMockTransport()) {
    const { mockArchiveSite } = await import('@/mocks/handlers');
    return mockArchiveSite(siteId);
  }
  return request<void>(`/sites/${siteId}/archive`, { method: 'POST' });
}

export async function createDeviceGroup(body: DeviceGroupCreate): Promise<DeviceGroup> {
  if (isMockTransport()) {
    const { mockCreateDeviceGroup } = await import('@/mocks/handlers');
    return mockCreateDeviceGroup(body);
  }
  return request<DeviceGroup>('/device-groups', { method: 'POST', body });
}

export async function updateDeviceGroup(
  deviceGroupId: Uuid,
  body: DeviceGroupUpdate,
): Promise<DeviceGroup> {
  if (isMockTransport()) {
    const { mockUpdateDeviceGroup } = await import('@/mocks/handlers');
    return mockUpdateDeviceGroup(deviceGroupId, body);
  }
  return request<DeviceGroup>(`/device-groups/${deviceGroupId}`, { method: 'PATCH', body });
}

export async function archiveDeviceGroup(deviceGroupId: Uuid): Promise<void> {
  if (isMockTransport()) {
    const { mockArchiveDeviceGroup } = await import('@/mocks/handlers');
    return mockArchiveDeviceGroup(deviceGroupId);
  }
  return request<void>(`/device-groups/${deviceGroupId}/archive`, { method: 'POST' });
}
